import React, { useState, useEffect } from 'react';
import { Mail, Download, Trash2, Loader2, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { emailsAPI } from '../../services/api';

const EmailsManager = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    try {
      const res = await emailsAPI.getAll();
      setEmails(res.data);
    } catch (e) {
      console.error('Error fetching emails:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await emailsAPI.delete(deleteId);
      setEmails(emails.filter(e => e.id !== deleteId));
      setDeleteId(null);
    } catch (e) {
      console.error('Error deleting email:', e);
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await emailsAPI.exportCSV();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forged-supps-emails-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting CSV:', e);
    }
  };

  const filteredEmails = emails.filter(e =>
    e.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (e.source || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Email List</h1>
          <p className="text-zinc-400">{emails.length} emails collected</p>
        </div>
        <Button
          onClick={handleExportCSV}
          className="bg-amber-500 hover:bg-amber-600 text-black"
          disabled={emails.length === 0}
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <Input
          placeholder="Search emails..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-zinc-800 border-zinc-700 text-white"
        />
      </div>

      {filteredEmails.length === 0 ? (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-12 text-center">
          <Mail className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">{emails.length === 0 ? 'No emails collected yet' : 'No matching emails'}</p>
          <p className="text-zinc-500 text-sm mt-1">Emails are collected when customers checkout via WhatsApp</p>
        </div>
      ) : (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-zinc-400 text-sm font-medium px-4 py-3">Email</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-4 py-3 hidden sm:table-cell">Source</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-4 py-3 hidden md:table-cell">Date</th>
                <th className="text-right text-zinc-400 text-sm font-medium px-4 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filteredEmails.map((item) => (
                <tr key={item.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="px-4 py-3">
                    <span className="text-white">{item.email}</span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-zinc-400 text-sm">{item.source || 'checkout'}</span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-zinc-500 text-sm">{new Date(item.date).toLocaleDateString()}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-400 hover:text-red-300"
                      onClick={() => setDeleteId(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Email</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to remove this email from your list?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmailsManager;
